// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyEnquiry, TyEnquiryStatus, TyOrganization } from "@/types";
import EnquiryCard from "@/UI/cards/EnquiryCard";
import ActionsBar, { type TyBarTab } from "@/UI/components/ActionsBar";
import ListPanel from "@/UI/components/ListPanel";
import Paginator from "@/UI/components/Paginator";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import EnquiryPopup from "@/UI/popups/EnquiryPopup";
import OrganizationFormPopup from "@/UI/popups/OrganizationFormPopup";
import OwnerFormPopup from "@/UI/popups/OwnerFormPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "all" | "new";

/**
 * Everybody who has asked for an account from `lozicore.com`, and the place one of them becomes a
 * company.
 *
 * **Two views and no figures.** Everything, and the ones nobody has opened — that is the whole of
 * what a list of messages is filtered by, and a row of stat cards over four of them would be
 * reporting on an inbox rather than helping anybody empty it. The one number worth saying is in the
 * panel's own line.
 *
 * **Converting is three calls, not one.** The company form opens filled in with what they wrote, the
 * owner form follows for the company it just made, and the enquiry is marked done. There is no
 * `convert` endpoint behind it on purpose — creating a company and seating its first owner already
 * have one each, with rules of their own, and a third doing both would be those rules written twice.
 * §6 of `ai.plans/enquiries-feature.md` is the argument.
 *
 * Nobody here creates an enquiry — somebody on the site does — and nothing deletes one, like
 * everywhere else under `/admins`: `done` is what "nothing left to do about it" looks like.
 */
export default function EnquiriesScreen() {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);
    const requestClose = usePopup((state) => state.requestClose);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const tab = (params.get("tab") as TyTab) || "all";
    const search = params.get("search") ?? "";
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);

    const list = useFetch<{ enquiries: TyEnquiry[]; total: number; unread: number }>("/enquiries", {}, false);
    /** The one write anything on this screen does: moving a status. */
    const move = useFetch("/enquiries", {}, false);

    const load = () =>
        list.invoke({ query: { status: tab === "new" ? "new" : undefined, search: search.trim() || undefined, page, limit: pageSize } });

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, search, page, pageSize]);

    useEffect(() => {
        if (!move.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [move.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        // Narrowing a list — or changing tab — puts you back at its start.
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const rows = list.data?.enquiries ?? [];
    const tabs: TyBarTab[] = (["all", "new"] as TyTab[]).map((key) => ({ key, label: t.enquiries.tabs[key] }));

    const moveStatus = (enquiry: TyEnquiry, status: TyEnquiryStatus) =>
        move.invoke({ method: "PATCH", path: `/enquiries/${enquiry._id}`, data: { status } });

    /**
     * The company first, then its first owner, then the stamp. Each step is refusable on its own —
     * a slug already taken stops at the first, and a company left without an owner is a state the
     * organizations screen already knows how to show and fix.
     */
    const convert = (enquiry: TyEnquiry) => {
        requestClose("enquiry");

        addComp({
            name: "organization-form",
            Component: OrganizationFormPopup,
            anim: "scale",
            details: {
                organization: null,
                prefill: { name: enquiry.organizationName, phone: enquiry.phone },
                onDone: (organization: TyOrganization) =>
                    addComp({
                        name: "owner-form",
                        Component: OwnerFormPopup,
                        anim: "scale",
                        details: {
                            organization,
                            prefill: { name: enquiry.clientName, phone: enquiry.phone },
                            /** The company exists and somebody can sign into it, so there is
                             *  nothing left to do about the message that asked for it. */
                            onDone: () => moveStatus(enquiry, "done"),
                        },
                    }),
            },
        });
    };

    /** One panel for the whole enquiry. Opening it is what "read" means, so it says so. */
    const openEnquiry = (enquiry: TyEnquiry) => {
        if (enquiry.status === "new") moveStatus(enquiry, "read");

        addComp({
            name: "enquiry",
            Component: EnquiryPopup,
            anim: "scale",
            details: {
                enquiry: { ...enquiry, status: enquiry.status === "new" ? "read" : enquiry.status },
                onStatus: (status: TyEnquiryStatus) => moveStatus(enquiry, status),
                onConvert: () => convert(enquiry),
            },
        });
    };

    return (
        <section className="list-screen">
            <ListPanel
                title={t.enquiries.title}
                subtitle={t.enquiries.subtitle.replace("{new}", String(list.data?.unread ?? 0)).replace("{total}", String(list.data?.total ?? 0))}
            >
                <ActionsBar
                    tabs={tabs}
                    activeTab={tab}
                    onTab={(key) => apply({ tab: key === "all" ? null : key })}
                    isFilterOpen={isFiltering}
                    onFilterOpen={() => {
                        setDraftSearch(search);
                        setIsFiltering(true);
                    }}
                    hasFilters={!!search}
                    onFilterApply={() => {
                        setIsFiltering(false);
                        apply({ search: draftSearch.trim() || null });
                    }}
                    onFilterClear={() => {
                        setDraftSearch("");
                        setIsFiltering(false);
                        apply({ search: null });
                    }}
                    onFilterCancel={() => setIsFiltering(false)}
                    filterContent={
                        <FilterSearchInput
                            value={draftSearch}
                            onChange={setDraftSearch}
                            label={t.common.search}
                            placeholder={t.enquiries.searchHint}
                        />
                    }
                />
            </ListPanel>

            {list.loading && !rows.length && <p className="screen-state">{t.common.loading}</p>}
            {(list.error || move.error) && <p className="screen-state screen-state--error">{list.error ?? move.error}</p>}
            {!list.loading && !list.error && !rows.length && <p className="screen-state">{t.enquiries.empty}</p>}

            <div className="enquiries-screen__grid">
                {rows.map((enquiry) => (
                    <EnquiryCard key={enquiry._id} enquiry={enquiry} onOpen={() => openEnquiry(enquiry)} onConvert={() => convert(enquiry)} />
                ))}
            </div>

            <Paginator
                page={page}
                total={list.data?.total ?? 0}
                count={rows.length}
                onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
            />
        </section>
    );
}
